/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect } from "react";

// react-github-btn
import GitHubButton from "react-github-btn";

// @mui material component
import Divider from "@mui/material/Divider";
import Switch from "@mui/material/Switch";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";

// @mui icons
import TwitterIcon from "@mui/icons-material/Twitter";
import FacebookIcon from "@mui/icons-material/Facebook";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

// Custom styles for the Configurator
import ConfiguratorRoot from "examples/Configurator/ConfiguratorRoot";

// Material Dashboard 2 React context
import {
  useMaterialUIController,
  setOpenConfigurator,
  setTransparentSidenav,
  setWhiteSidenav,
  setPurpleSidenav,
  setFixedNavbar,
  setSidenavColor,
  setDarkMode,
} from "context";

import { setGreenSidenav } from "../../context"; // Importa la nueva función

function Configurator() {
  const [controller, dispatch] = useMaterialUIController();
  const {
    openConfigurator,
    fixedNavbar,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    purpleSidenav,
    darkMode,
    greenSidenav,
  } = controller;
  const [disabled, setDisabled] = useState(false);
  const sidenavColors = ["primary", "dark", "info", "success", "warning", "error", "purple"];

  // Use the useEffect hook to change the button state for the sidenav type based on window size.
  useEffect(() => {
    // A function that sets the disabled state of the buttons for the sidenav type.
    function handleDisabled() {
      return window.innerWidth > 1200 ? setDisabled(false) : setDisabled(true);
    }

    // The event listener that's calling the handleDisabled function when resizing the window.
    window.addEventListener("resize", handleDisabled);

    // Call the handleDisabled function to set the state with the initial value.
    handleDisabled();

    // Remove event listener on cleanup
    return () => window.removeEventListener("resize", handleDisabled);
  }, []);

  const handleCloseConfigurator = () => setOpenConfigurator(dispatch, false);
  const handleTransparentSidenav = () => {
    setTransparentSidenav(dispatch, true);
    setWhiteSidenav(dispatch, false);
    setPurpleSidenav(dispatch, false);
    setDarkMode(dispatch, false);
  };
  const handleWhiteSidenav = () => {
    setWhiteSidenav(dispatch, true);
    setTransparentSidenav(dispatch, false);
    setPurpleSidenav(dispatch, false);
    setDarkMode(dispatch, false);
  };
  const handlePurpleSidenav = () => {
    setPurpleSidenav(dispatch, true);
    setTransparentSidenav(dispatch, false);
    setWhiteSidenav(dispatch, false);
    setDarkMode(dispatch, false);
  };
  const handleDarkSidenav = () => {
    setWhiteSidenav(dispatch, false);
    setTransparentSidenav(dispatch, false);
    setPurpleSidenav(dispatch, false);
    setGreenSidenav(dispatch, false);
    setDarkMode(dispatch, true);
  };
  const handleFixedNavbar = () => setFixedNavbar(dispatch, !fixedNavbar);
  const handleDarkMode = () => setDarkMode(dispatch, !darkMode);

  const handleGreenSidenav = () => {
    setGreenSidenav(dispatch, true);
    setWhiteSidenav(dispatch, false);
    setTransparentSidenav(dispatch, false);
    setPurpleSidenav(dispatch, false);
    setDarkMode(dispatch, false);
  };

  return (
    <ConfiguratorRoot variant="permanent" ownerState={{ openConfigurator }}>
      <MDBox
        display="flex"
        justifyContent="space-between"
        alignItems="baseline"
        pt={4}
        pb={0.5}
        px={3}
      >
        <MDBox>
          <MDTypography variant="h5">Material UI Configurator</MDTypography>
          <MDTypography variant="body2" color="text">
            See our dashboard options.
          </MDTypography>
        </MDBox>

        <Icon
          sx={({ typography: { size }, palette: { dark, white } }) => ({
            fontSize: `${size.lg} !important`,
            color: darkMode ? white.main : dark.main,
            stroke: "currentColor",
            strokeWidth: "2px",
            cursor: "pointer",
            transform: "translateY(5px)",
          })}
          onClick={handleCloseConfigurator}
        >
          close
        </Icon>
      </MDBox>

      <Divider />

      <MDBox pt={0.5} pb={3} px={3}>
        <MDBox>
          <MDTypography variant="h6">Sidenav Colors</MDTypography>

          <MDBox mb={0.5}>
            {sidenavColors.map((color) => (
              <IconButton
                key={color}
                sx={(theme) => {
                  const { palette, borders, transitions } = theme;
                  const { white, dark, background, gradients } = palette;
                  const { borderWidth } = borders;
                  const { linearGradient } = theme.functions;

                  return {
                  width: "24px",
                  height: "24px",
                  padding: 0,
                  border: `${borderWidth[1]} solid ${darkMode ? background.sidenav : white.main}`,
                  borderColor: () => {
                    let borderColorValue = sidenavColor === color && dark.main;

                    if (darkMode && sidenavColor === color) {
                      borderColorValue = white.main;
                    }

                    return borderColorValue;
                  },
                  transition: transitions.create("border-color", {
                    easing: transitions.easing.sharp,
                    duration: transitions.duration.shorter,
                  }),
                    backgroundImage: linearGradient(gradients[color].main, gradients[color].state),

                  "&:not(:last-child)": {
                    mr: 1,
                  },

                  "&:hover, &:focus, &:active": {
                    borderColor: darkMode ? white.main : dark.main,
                  },
                  };
                }}
                onClick={() => setSidenavColor(dispatch, color)}
              />
            ))}
          </MDBox>
        </MDBox>

        <MDBox mt={3} lineHeight={1}>
          <MDTypography variant="h6">Sidenav Type</MDTypography>
          <MDTypography variant="button" color="text">
            Choose between different sidenav types.
          </MDTypography>

          <MDBox
            sx={{
              display: "flex",
              mt: 2,
              mr: 1,
            }}
          >
            <MDButton
              color="dark"
              variant="gradient"
              onClick={handleDarkSidenav}
              disabled={disabled}
              fullWidth
              sx={(theme) => {
                const { palette, functions, borders } = theme;
                const { white, dark, background, gradients } = palette;
                const { linearGradient, pxToRem } = functions;
                const { borderWidth } = borders;

                return {
                  height: pxToRem(39),
                  background: (
                    !transparentSidenav &&
                    !whiteSidenav &&
                    !purpleSidenav &&
                    !greenSidenav
                  )
                    ? darkMode
                      ? white.main
                      : linearGradient(gradients.dark.main, gradients.dark.state)
                    : darkMode
                    ? background.sidenav
                    : white.main,
                  color: (
                    !transparentSidenav &&
                    !whiteSidenav &&
                    !purpleSidenav &&
                    !greenSidenav
                  )
                    ? darkMode
                      ? gradients.dark.main
                      : white.main
                    : darkMode
                    ? white.main
                    : dark.main,
                  border: `${borderWidth[1]} solid ${
                    !transparentSidenav &&
                    !whiteSidenav &&
                    !purpleSidenav &&
                    !greenSidenav &&
                    (darkMode ? white.main : dark.main)
                  }`,
                  "&:hover, &:focus, &:focus:not(:hover)": {
                    background: (
                      !transparentSidenav &&
                      !whiteSidenav &&
                      !purpleSidenav &&
                      !greenSidenav
                    )
                      ? darkMode
                        ? white.main
                        : linearGradient(gradients.dark.main, gradients.dark.state)
                      : darkMode
                      ? background.sidenav
                      : white.main,
                    color: (
                      !transparentSidenav &&
                      !whiteSidenav &&
                      !purpleSidenav &&
                      !greenSidenav
                    )
                      ? darkMode
                        ? gradients.dark.main
                        : white.main
                      : darkMode
                      ? white.main
                      : dark.main,
                    border: `${borderWidth[1]} solid ${
                      !transparentSidenav &&
                      !whiteSidenav &&
                      !purpleSidenav &&
                      !greenSidenav &&
                      (darkMode ? white.main : dark.main)
                    }`,
                  },
                };
              }}
            >
              Dark
            </MDButton>
            <MDBox sx={{ mx: 1, width: "8rem", minWidth: "8rem" }}>
              <MDButton
                color="dark"
                variant="gradient"
                onClick={handleTransparentSidenav}
                disabled={disabled}
                fullWidth
                sx={(theme) => {
                  const { palette, functions, borders } = theme;
                  const { white, dark, background, transparent } = palette;
                  const { linearGradient, pxToRem } = functions;
                  const { borderWidth } = borders;

                  return {
                    height: pxToRem(39),
                    background: transparentSidenav
                      ? darkMode
                        ? white.main
                        : transparent.main
                      : darkMode
                      ? background.sidenav
                      : white.main,
                    color: transparentSidenav
                      ? darkMode
                        ? transparent.main
                        : white.main
                      : darkMode
                      ? white.main
                      : dark.main,
                    border: `${borderWidth[1]} solid ${
                      transparentSidenav &&
                      (darkMode ? white.main : dark.main)
                    }`,
                    "&:hover, &:focus, &:focus:not(:hover)": {
                      background: transparentSidenav
                        ? darkMode
                          ? white.main
                          : transparent.main
                        : darkMode
                        ? background.sidenav
                        : white.main,
                      color: transparentSidenav
                        ? darkMode
                          ? transparent.main
                        : white.main
                      : darkMode
                      ? white.main
                      : dark.main,
                      border: `${borderWidth[1]} solid ${
                        transparentSidenav &&
                        (darkMode ? white.main : dark.main)
                      }`,
                    },
                  };
                }}
              >
                Transparent
              </MDButton>
            </MDBox>
            <MDButton
              color="dark"
              variant="gradient"
              onClick={handleWhiteSidenav}
              disabled={disabled}
              fullWidth
              sx={(theme) => {
                const { palette, functions, borders } = theme;
                const { white, dark, background } = palette;
                const { linearGradient, pxToRem } = functions;
                const { borderWidth } = borders;

                return {
                  height: pxToRem(39),
                  background: whiteSidenav
                    ? darkMode
                      ? white.main
                      : white.main
                    : darkMode
                    ? background.sidenav
                    : white.main,
                  color: whiteSidenav
                    ? darkMode
                      ? dark.main
                      : dark.main
                    : darkMode
                    ? white.main
                    : dark.main,
                  border: `${borderWidth[1]} solid ${
                    whiteSidenav && (darkMode ? white.main : dark.main)
                  }`,
                  "&:hover, &:focus, &:focus:not(:hover)": {
                    background: whiteSidenav
                      ? darkMode
                        ? white.main
                        : white.main
                      : darkMode
                      ? background.sidenav
                      : white.main,
                    color: whiteSidenav
                      ? darkMode
                        ? dark.main
                        : dark.main
                      : darkMode
                      ? white.main
                      : dark.main,
                  },
                };
              }}
            >
              White
            </MDButton>
            <MDBox sx={{ mx: 1, width: "8rem", minWidth: "8rem" }}>
              <MDButton
                color="dark"
                variant="gradient"
                onClick={handlePurpleSidenav}
                disabled={disabled}
                fullWidth
                sx={(theme) => {
                  const { palette, functions, borders } = theme;
                  const { white, dark, background, gradients } = palette;
                  const { linearGradient, pxToRem } = functions;
                  const { borderWidth } = borders;

                  return {
                    height: pxToRem(39),
                    background: purpleSidenav
                      ? darkMode
                        ? white.main
                        : linearGradient(gradients.purple.main, gradients.purple.state)
                      : darkMode
                      ? background.sidenav
                      : white.main,
                    color: purpleSidenav
                      ? darkMode
                        ? gradients.purple.main
                        : white.main
                      : darkMode
                      ? white.main
                      : dark.main,
                    border: `${borderWidth[1]} solid ${
                      purpleSidenav && (darkMode ? white.main : dark.main)
                    }`,
                    "&:hover, &:focus, &:focus:not(:hover)": {
                      background: purpleSidenav
                        ? darkMode
                          ? white.main
                          : linearGradient(gradients.purple.main, gradients.purple.state)
                        : darkMode
                        ? background.sidenav
                        : white.main,
                      color: purpleSidenav
                        ? darkMode
                          ? gradients.purple.main
                        : white.main
                      : darkMode
                      ? white.main
                      : dark.main,
                    },
                  };
                }}
              >
                Purple
              </MDButton>
            </MDBox>
            <MDBox sx={{ mx: 1, width: "8rem", minWidth: "8rem" }}>
              <MDButton
                color="dark"
                variant="gradient"
                onClick={handleGreenSidenav}
                disabled={disabled}
                fullWidth
                sx={(theme) => {
                  const { palette, functions, borders } = theme;
                  const { white, dark, background, gradients } = palette;
                  const { linearGradient, pxToRem } = functions;
                  const { borderWidth } = borders;

                  return {
                    height: pxToRem(39),
                    background: greenSidenav
                      ? darkMode
                        ? white.main
                        : linearGradient(gradients.success.main, gradients.success.state)
                      : darkMode
                      ? background.sidenav
                      : white.main,
                    color: greenSidenav
                      ? darkMode
                        ? gradients.success.main
                        : white.main
                      : darkMode
                      ? white.main
                      : dark.main,
                    border: `${borderWidth[1]} solid ${
                      greenSidenav && (darkMode ? white.main : dark.main)
                    }`,
                    "&:hover, &:focus, &:focus:not(:hover)": {
                      background: greenSidenav
                        ? darkMode
                          ? white.main
                          : linearGradient(gradients.success.main, gradients.success.state)
                        : darkMode
                        ? background.sidenav
                        : white.main,
                      color: greenSidenav
                        ? darkMode
                          ? gradients.success.main
                        : white.main
                      : darkMode
                      ? white.main
                      : dark.main,
                    },
                  };
                }}
              >
                Green
              </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt={3}
          lineHeight={1}
        >
          <MDTypography variant="h6">Navbar Fixed</MDTypography>

          <Switch checked={fixedNavbar} onChange={handleFixedNavbar} />
        </MDBox>
        <Divider />
        <MDBox display="flex" justifyContent="space-between" alignItems="center" lineHeight={1}>
          <MDTypography variant="h6">Light / Dark</MDTypography>

          <Switch checked={darkMode} onChange={handleDarkMode} />
        </MDBox>
        <Divider />
        <MDBox mt={3} mb={2}>
          <MDButton
            component={Link}
            href="https://www.creative-tim.com/learning-lab/react/quick-start/material-dashboard/"
            target="_blank"
            rel="noreferrer"
            color={darkMode ? "light" : "dark"}
            variant="outlined"
            fullWidth
          >
            view documentation
          </MDButton>
        </MDBox>
        <MDBox display="flex" justifyContent="center">
          <GitHubButton
            href="https://github.com/creativetimofficial/material-dashboard-react"
            data-icon="octicon-star"
            data-size="large"
            data-show-count="true"
            aria-label="Star creativetimofficial/material-dashboard-react on GitHub"
          >
            Star
          </GitHubButton>
        </MDBox>
        <MDBox mt={2} textAlign="center">
            <MDTypography variant="h6">Thank you for sharing!</MDTypography>

          <MDBox display="flex" justifyContent="center">
              <MDButton
                component={Link}
                href="//twitter.com/intent/tweet?text=Check%20Material%20Dashboard%20React%20made%20by%20%40CreativeTim%20%23webdesign%20%23dashboard%20%23react%20%mui&url=https%3A%2F%2Fwww.creative-tim.com%2Fproduct%2Fmaterial-dashboard-react"
                target="_blank"
                rel="noreferrer"
                color="dark"
              >
                <TwitterIcon />
                &nbsp; Tweet
              </MDButton>
            <MDBox mr={1.5}>
            <MDButton
              component={Link}
              href="https://www.facebook.com/sharer/sharer.php?u=https://www.creative-tim.com/product/material-dashboard-react"
              target="_blank"
              rel="noreferrer"
              color="dark"
            >
              <FacebookIcon />
              &nbsp; Share
            </MDButton>
            </MDBox>
          </MDBox>
        </MDBox>
      </MDBox>
    </ConfiguratorRoot>
  );
}

export default Configurator;
